import { useState } from 'react'
import { usePushNotifications } from '../hooks/usePushNotifications'

// State capitals used as representative coordinates when geolocation is denied.
// The PostGIS spatial filter already handles this: state-wide alerts contain
// the capital point; local alerts near the capital also match.
const AU_STATES = [
  { code: 'NSW', label: 'New South Wales',            lat: -33.8688, lon: 151.2093 },
  { code: 'VIC', label: 'Victoria',                   lat: -37.8136, lon: 144.9631 },
  { code: 'QLD', label: 'Queensland',                 lat: -27.4698, lon: 153.0251 },
  { code: 'WA',  label: 'Western Australia',          lat: -31.9505, lon: 115.8605 },
  { code: 'SA',  label: 'South Australia',            lat: -34.9285, lon: 138.6007 },
  { code: 'TAS', label: 'Tasmania',                   lat: -42.8821, lon: 147.3272 },
  { code: 'NT',  label: 'Northern Territory',         lat: -12.4634, lon: 130.8456 },
  { code: 'ACT', label: 'Australian Capital Territory', lat: -35.2809, lon: 149.1300 },
]

function getLocation(): Promise<{ lat: number; lon: number } | null> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) { resolve(null); return }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => resolve(null),
      { timeout: 8000, maximumAge: 300_000 },
    )
  })
}

type Flow = 'idle' | 'busy' | 'needs-picker'

export default function PushNotificationButton() {
  const { state, subscribe, unsubscribe } = usePushNotifications()
  const [flow, setFlow] = useState<Flow>('idle')
  const [selectedState, setSelectedState] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (state === 'loading' || state === 'unsupported') return null

  const handleBellClick = async () => {
    if (state === 'subscribed') {
      try { await unsubscribe() } catch { /* ignore */ }
      return
    }
    if (state === 'denied') return

    setError(null)
    setFlow('busy')

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      setFlow('idle')
      return
    }

    const location = await getLocation()
    if (location) {
      try {
        await subscribe(location.lat, location.lon)
      } catch (e) {
        setError('Subscription failed. Please try again.')
      }
      setFlow('idle')
    } else {
      // Geolocation denied or unavailable — show state picker
      setFlow('needs-picker')
    }
  }

  const handlePickerSubscribe = async () => {
    const chosen = AU_STATES.find((s) => s.code === selectedState)
    if (!chosen) return
    setError(null)
    setFlow('busy')
    try {
      await subscribe(chosen.lat, chosen.lon)
    } catch {
      setError('Subscription failed. Please try again.')
    }
    setFlow('idle')
    setSelectedState('')
  }

  const handlePickerCancel = () => {
    setFlow('idle')
    setSelectedState('')
  }

  const active = state === 'subscribed'
  const blocked = state === 'denied'

  return (
    <>
      <button
        type="button"
        onClick={handleBellClick}
        disabled={flow === 'busy' || blocked}
        title={
          blocked    ? 'Notifications blocked — enable in browser settings' :
          active     ? 'Notifications on — click to turn off' :
          flow === 'busy' ? 'Please wait…' :
          'Turn on push notifications'
        }
        className={`transition-colors ${
          blocked || flow === 'busy' ? 'cursor-not-allowed opacity-40' : 'hover:text-gray-700 dark:hover:text-gray-300'
        } ${active ? 'text-orange-500 dark:text-orange-400' : 'text-gray-500 dark:text-gray-600'}`}
      >
        {active ? (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
          </svg>
        ) : (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
          </svg>
        )}
      </button>

      {/* State picker — shown when geolocation is unavailable */}
      {flow === 'needs-picker' && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40">
          <div className="bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-700 rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Choose your state or territory</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Location access was not granted. Select your state and we'll use your capital city
                as a reference point for nearby alerts.
              </p>
            </div>

            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full bg-gray-100 border border-gray-300 text-gray-800 focus:border-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 dark:focus:border-gray-500 rounded-lg px-3 py-2 text-sm focus:outline-none"
            >
              <option value="">Select state or territory…</option>
              {AU_STATES.map((s) => (
                <option key={s.code} value={s.code}>{s.label}</option>
              ))}
            </select>

            {error && <p className="text-xs text-red-400">{error}</p>}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handlePickerSubscribe}
                disabled={!selectedState}
                className="flex-1 py-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
              >
                Subscribe
              </button>
              <button
                type="button"
                onClick={handlePickerCancel}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
